// src/components/ProcessingResults/ToolsContainer.jsx
import React, { useState } from 'react';
import { Card, Button, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import './ToolsContainer.css';

const ToolsContainer = ({
  activeStage,
  onShowInfo,
  onDownloadOriginal,
  onRotateGrid,
  onRotateImage,
  onColorModeChange,
  colorMode,
  selectedCells,
  onClearSelection,
  imagesLoaded,
  onCellSizeAdjustmentChange,
  onAddCells,
  onDeleteCells,
  onBrighten,
  onDarken,
  onResetColors
}) => {
  const [showAddCellModal, setShowAddCellModal] = useState(false);
  const [showDeleteCellModal, setShowDeleteCellModal] = useState(false);

  const renderTooltip = (text) => (
    <Tooltip>{text}</Tooltip>
  );

  const isStageActive = (stageIndex) => activeStage === stageIndex;

  const handleShowInfo = () => {
     onShowInfo();
  };

  const handleSizeIncrease = () => {
    const newAdjustment = 0.001;
    onCellSizeAdjustmentChange(newAdjustment);
  };

  const handleSizeDecrease = () => {
    const newAdjustment = -0.001;
    onCellSizeAdjustmentChange(newAdjustment);
  };

  const handleBrightnessIncrease = () => {
    const increaseFactor = 0.1;
    onBrighten(increaseFactor);
  };

  const handleBrightnessDecrease = () => {
    const decreaseFactor = 0.1;
    onDarken(decreaseFactor);
  };

  const handleBrightnessReset = () => {
    onResetColors();
  };

  const handleAddCells = (direction) => {
    onAddCells(direction);
    setShowAddCellModal(false);
  };

  const handleDeleteCells = (direction) => {
    onDeleteCells(direction);
    setShowDeleteCellModal(false);
  };

  return (
      <div className="tools-container">
        {/* Original Image Tools */}
        <div className={`tools-group ${isStageActive(0) ? 'active' : 'inactive'}`}>
          <div className="tools-content">
            <OverlayTrigger placement="right" overlay={renderTooltip("Show info")}>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={!isStageActive(0)}
                className="tool-button"
                onClick={() => handleShowInfo()}
              >
                <i className="bi bi-info-square"></i>
              </Button>
            </OverlayTrigger>

            <OverlayTrigger placement="right" overlay={renderTooltip("Download original image")}>
              <Button
                variant="outline-success"
                size="sm"
                disabled={!isStageActive(0)}
                className="tool-button"
                onClick={() => onDownloadOriginal()}
              >
                <i className="bi bi-download"></i>
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Divider Line */}
        <div className="tools-divider"></div>

        {/* Cropped Image Tools */}
        <div className={`tools-group ${isStageActive(1) ? 'active' : 'inactive'}`}>
          <div className="tools-content">
            <OverlayTrigger placement="right" overlay={renderTooltip("View cropped image")}>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={!isStageActive(1)}
                className="tool-button"
              >
                <i className="bi bi-crop"></i>
              </Button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Divider Line */}
        <div className="tools-divider"></div>

        {/* Rotated & Detected Tools */}
        <div className={`tools-group ${isStageActive(2) ? 'active' : 'inactive'}`}>
          <div className="tools-content">
            {/* Image Rotation Tools */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate image 90° clockwise")}>
                  <Button
                    variant="outline-info"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateImage(90)}
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate image 90° counterclockwise")}>
                  <Button
                    variant="outline-info"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateImage(-90)}
                  >
                    <i className="bi bi-arrow-counterclockwise"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate image 180°")}>
                  <Button
                    variant="outline-info"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateImage(180)}
                  >
                    <i className="bi bi-arrow-repeat"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            {/* Grid Rotation Tools */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate grid 90° clockwise")}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateGrid(90)}
                  >
                    <i className="bi bi-grid"></i>
                    <i className="bi bi-arrow-clockwise position-absolute" style={{fontSize: '8px', top: '2px', right: '2px'}}></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate grid 90° counterclockwise")}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateGrid(-90)}
                  >
                    <i className="bi bi-grid"></i>
                    <i className="bi bi-arrow-counterclockwise position-absolute" style={{fontSize: '8px', top: '2px', right: '2px'}}></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Rotate grid 180°")}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => onRotateGrid(180)}
                  >
                    <i className="bi bi-grid"></i>
                    <i className="bi bi-arrow-repeat position-absolute" style={{fontSize: '8px', top: '2px', right: '2px'}}></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            {/* Cell Size Adjustment */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Decrease cell size")}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={!isStageActive(2)}
                    className="tool-button"
                    onClick={handleSizeDecrease}
                  >
                    <i className="bi bi-dash-lg"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Increase cell size")}>
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={!isStageActive(2)}
                    className="tool-button"
                    onClick={handleSizeIncrease}
                  >
                    <i className="bi bi-plus-lg"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            {/* Grid Structure Tools */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Delete row or column")}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => setShowDeleteCellModal(true)}
                  >
                    <i className="bi bi-dash-square"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Add row or column")}>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    disabled={!isStageActive(2) || !imagesLoaded.rotated}
                    className="tool-button"
                    onClick={() => setShowAddCellModal(true)}
                  >
                    <i className="bi bi-plus-square"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </div>
        </div>

        {/* Divider Line */}
        <div className="tools-divider"></div>

        {/* Grid Analysis Tools */}
        <div className={`tools-group ${isStageActive(3) ? 'active' : 'inactive'}`}>
          <div className="tools-content">
            {/* Color Mode Selection */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Mean color mode")}>
                  <Button
                    variant={colorMode === 'mean' ? 'primary' : 'outline-secondary'}
                    size="sm"
                    disabled={!isStageActive(3)}
                    className="tool-button"
                    onClick={() => onColorModeChange('mean')}
                  >
                    <i className="bi bi-palette"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Median color mode")}>
                  <Button
                    variant={colorMode === 'median' ? 'primary' : 'outline-secondary'}
                    size="sm"
                    disabled={!isStageActive(3)}
                    className="tool-button"
                    onClick={() => onColorModeChange('median')}
                  >
                    <i className="bi bi-palette2"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>

            {/* Brightness Adjustment Tools */}
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Decrease brightness")}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!isStageActive(3)}
                    className="tool-button"
                    onClick={handleBrightnessDecrease}
                  >
                    <i className="bi bi-brightness-alt-low"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Reset brightness")}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!isStageActive(3)}
                    className="tool-button"
                    onClick={handleBrightnessReset}
                  >
                    <i className="bi bi-circle"></i>
                  </Button>
                </OverlayTrigger>

                <OverlayTrigger placement="right" overlay={renderTooltip("Increase brightness")}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={!isStageActive(3)}
                    className="tool-button"
                    onClick={handleBrightnessIncrease}
                  >
                    <i className="bi bi-brightness-alt-high"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </div>
        </div>

        {/* Selection Tools */}
        <div className="tools-divider"></div>
        <div className={`tools-group ${(isStageActive(2) || isStageActive(3)) && selectedCells.length > 0  ? 'active' : 'inactive'}`}>
          <div className="tools-content">
            <div className="tools-subgroup">
              <div className="tools-grid-auto">
                <OverlayTrigger placement="right" overlay={renderTooltip("Clear selection")}>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    disabled={(!isStageActive(3) && !isStageActive(2)) || selectedCells.length === 0}
                    className="tool-button"
                    onClick={onClearSelection}
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                </OverlayTrigger>
              </div>
            </div>
          </div>
        </div>

        {/* Modals remain the same */}
        {/* Add Cells Modal */}
        <Modal show={showAddCellModal} onHide={() => setShowAddCellModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Add Cells to Grid</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Choose where to add new cells:</p>
            <div className="d-grid gap-2">
              <Button
                variant="outline-primary"
                onClick={() => handleAddCells('top')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-up me-2"></i>
                Add Row at Top
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => handleAddCells('bottom')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-down me-2"></i>
                Add Row at Bottom
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => handleAddCells('left')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Add Column at Left
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => handleAddCells('right')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-right me-2"></i>
                Add Column at Right
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddCellModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Cells Modal */}
        <Modal show={showDeleteCellModal} onHide={() => setShowDeleteCellModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Delete Cells from Grid</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted mb-3">Choose which cells to delete:</p>
            <div className="d-grid gap-2">
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteCells('top')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-up me-2"></i>
                Delete Top Row
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteCells('bottom')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-down me-2"></i>
                Delete Bottom Row
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteCells('left')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Delete Left Column
              </Button>
              <Button
                variant="outline-danger"
                onClick={() => handleDeleteCells('right')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-right me-2"></i>
                Delete Right Column
              </Button>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteCellModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
};

export default ToolsContainer;